// The Dome, /site/book: pick a day, a length, a time and a court; check out; confirm; manage bookings.
// Plain TypeScript + DOM against the live API. Motion: GSAP, transform and opacity only; `?calm` is static.
import { mountChrome } from "../shared/chrome";
import gsap from "gsap";

// Same rule as shared/motion.ts motionOn(): motion forced on for review, `?calm` is the reduced version.
// Imported locally so this page does not register ScrollTrigger, which it does not use.
const motionOn = (): boolean => !new URLSearchParams(location.search).has("calm");
import { api, ApiError, type Availability, type Booking, type Duration, type Method, type Slot } from "./api";
import { Icon } from "./icons";
import { CourtPlan } from "../shared/plan";
import {
  MAPS,
  cleanPhone,
  dateLabel,
  dayNum,
  downloadIcs,
  emailOk,
  endTime,
  esc,
  isPast,
  next14,
  phoneOk,
  rp,
  store,
  weekday,
} from "./format";

mountChrome({ active: "book" });

// Club photos are served at /media by the review server; set at runtime so the HTML bundler does not try to resolve them.
document.querySelectorAll<HTMLImageElement>("img[data-media]").forEach((img) => (img.src = `/media/${img.dataset.media}`));

const M = motionOn();
if (!M) {
  document.documentElement.classList.add("bk-calm");
  gsap.ticker.sleep(); // calm: nothing animates, so no frame loop
}
const IN = "expo.out";
const PHONE_KEY = "thedome.phone";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const DAYPARTS: [Slot["daypart"], string][] = [
  ["morning", "Morning"],
  ["afternoon", "Afternoon"],
  ["evening", "Evening"],
];

type Step = "pick" | "checkout" | "done" | "mine";
const S = {
  step: "pick" as Step,
  days: next14(),
  date: "",
  duration: 90 as Duration,
  avail: null as Availability | null,
  load: "loading" as "loading" | "ok" | "error",
  time: null as string | null,
  court: null as number | null,
  split: false,
  booking: null as Booking | null,
  submitting: false,
  wantTime: null as string | null,
};
S.date = S.days[0];
let autoAdvance = false;

/* ---------- prefill from the query: ?date&time&duration ---------- */
{
  const q = new URLSearchParams(location.search);
  const d = q.get("date");
  if (d && S.days.includes(d)) S.date = d;
  else autoAdvance = true;
  const dur = Number(q.get("duration"));
  if (dur === 60 || dur === 90 || dur === 120) S.duration = dur;
  const t = q.get("time");
  if (t && /^\d{2}:\d{2}$/.test(t)) S.wantTime = t;
}

function syncUrl() {
  const q = new URLSearchParams(location.search);
  q.set("date", S.date);
  q.set("duration", String(S.duration));
  if (S.time) q.set("time", S.time);
  else q.delete("time");
  const qs = q.toString().replace(/calm=(&|$)/, "calm$1");
  history.replaceState(history.state, "", `${location.pathname}?${qs}${location.hash}`);
}

const slotOf = (t: string | null) => (t && S.avail ? S.avail.slots.find((s) => s.time === t) ?? null : null);
const sel = () => slotOf(S.time);

/* ---------- motion helpers ---------- */
function rise(targets: Element[] | NodeListOf<Element>, stagger = 0.05, y = 16) {
  if (!M) return;
  const list = Array.from(targets);
  if (!list.length) return;
  gsap.fromTo(list, { opacity: 0, y }, { opacity: 1, y: 0, duration: 0.6, ease: IN, stagger, clearProps: "transform,opacity" });
}

/** Price ticks: count from the last shown value to the new one. */
function tick(el: HTMLElement, to: number, render: (n: number) => string) {
  const anyEl = el as HTMLElement & { _tw?: gsap.core.Tween };
  anyEl._tw?.kill();
  const from = el.dataset.v ? Number(el.dataset.v) : to;
  el.dataset.v = String(to);
  if (!M || from === to) {
    el.textContent = render(to);
    return;
  }
  const o = { v: from };
  anyEl._tw = gsap.to(o, {
    v: to,
    duration: 0.7,
    ease: "power3.out",
    onUpdate: () => void (el.textContent = render(Math.round(o.v / 1000) * 1000)),
    onComplete: () => void (el.textContent = render(to)),
  });
}

/** Shared-element FLIP: the selected fill (a ::before) travels from the old choice into the new one. */
function slideFill(from: Element | null, to: Element | null) {
  if (!M || !from || !to || from === to) return;
  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  if (!b.width || !b.height) return;
  const el = to as HTMLElement;
  gsap.killTweensOf(el);
  el.classList.add("is-moving");
  gsap.fromTo(
    el,
    { "--fx": `${a.left - b.left}px`, "--fy": `${a.top - b.top}px`, "--sx": a.width / b.width, "--sy": a.height / b.height },
    {
      "--fx": "0px", "--fy": "0px", "--sx": 1, "--sy": 1, duration: 0.55, ease: "expo.out",
      onComplete: () => {
        el.classList.remove("is-moving");
        el.style.removeProperty("--fx"); el.style.removeProperty("--fy"); el.style.removeProperty("--sx"); el.style.removeProperty("--sy");
      },
    },
  );
}
/** Set aria-pressed across a group and slide the fill to the new choice. */
function pressIn(group: Element, sel: string, isOn: (b: HTMLElement) => boolean) {
  const prev = group.querySelector('[aria-pressed="true"]');
  let next: Element | null = null;
  group.querySelectorAll<HTMLElement>(sel).forEach((b) => {
    const on = isOn(b);
    b.setAttribute("aria-pressed", String(on));
    if (on) next = b;
  });
  slideFill(prev, next);
}

/* ---------- date strip ---------- */
const datesEl = $("dates");
datesEl.innerHTML = S.days
  .map(
    (d, i) => `<button type="button" class="tile fx" data-date="${d}" aria-pressed="${d === S.date}" aria-label="${i === 0 ? "Today, " : i === 1 ? "Tomorrow, " : ""}${dateLabel(d)}">
      <span class="caption chip-wd">${i === 0 ? "Today" : weekday(d)}</span>
      <span class="body-strong num chip-d">${dayNum(d)}</span>
    </button>`,
  )
  .join("");
datesEl.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-date]");
  if (b) setDate(b.dataset.date!);
});
function paintDates(scroll = true) {
  void scroll;
  pressIn(datesEl, "[data-date]", (b) => b.dataset.date === S.date);
}
function setDate(d: string) {
  if (d === S.date) return;
  S.date = d;
  S.time = null;
  S.court = null;
  paintDates();
  syncUrl();
  updateBar();
  load();
}

/* ---------- duration ---------- */
const lenSeg = $("len-seg");
lenSeg.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-dur]");
  if (!b) return;
  const d = Number(b.dataset.dur) as Duration;
  if (d === S.duration) return;
  S.duration = d;
  paintLen();
  syncUrl();
  load(true);
});
function paintLen() {
  pressIn(lenSeg, "[data-dur]", (b) => Number(b.dataset.dur) === S.duration);
}

/* ---------- times ---------- */
const timesEl = $("times");
let ctrl: AbortController | null = null;

async function load(keepSelection = false) {
  ctrl?.abort();
  ctrl = new AbortController();
  const mine = ctrl;
  S.load = "loading";
  if (!keepSelection) {
    S.time = null;
    S.court = null;
  }
  renderTimes();
  try {
    const res = await api.availability(S.date, S.duration, mine.signal);
    if (mine !== ctrl) return;
    S.avail = res;
    S.load = "ok";
    // keep the chosen time and court only while they are still free
    const s = sel();
    if (!s || s.courtsFree === 0) {
      S.time = null;
      S.court = null;
    } else if (S.court && !s.courts.some((c) => c.id === S.court && c.free)) S.court = null;
    if (S.wantTime) {
      const w = slotOf(S.wantTime);
      if (w && w.courtsFree > 0) S.time = w.time;
      S.wantTime = null;
    }
  } catch (e) {
    if ((e as Error).name === "AbortError" || mine !== ctrl) return;
    S.avail = null;
    S.load = "error";
  }
  // late in the day with nothing left: open on tomorrow instead of a dead end (only when no date was asked for)
  if (autoAdvance && S.load === "ok" && S.date === S.days[0] && !S.avail?.slots.some((x) => x.courtsFree > 0)) {
    autoAdvance = false;
    S.date = S.days[1];
    paintDates();
    return load(true);
  }
  autoAdvance = false;
  renderTimes();
  syncUrl();
  updateBar();
  updateSide();
  if (S.time) timesEl.querySelector<HTMLElement>(`[data-time="${S.time}"]`)?.scrollIntoView({ block: "center", behavior: M ? "smooth" : "auto" });
}

function courtsWord(n: number) {
  return n === 0 ? "Fully booked" : n === 1 ? "1 court free" : `${n} courts free`;
}

/** One filled time tile: the time large, the free courts in Court Blue ink. */
function slotTile(s: Slot) {
  const full = s.courtsFree === 0;
  const on = s.time === S.time;
  return `<button type="button" class="slot fx${on ? " is-sel" : ""}" data-time="${s.time}" aria-pressed="${on}" ${full ? "disabled" : ""}
      aria-label="${s.time}, ${courtsWord(s.courtsFree)}${full ? "" : `, ${rp(s.price)}`}">
    <span class="num slot-t">${s.time}</span>
    <span class="slot-c num">${full ? "Full" : `${s.courtsFree} free`}</span>
  </button>`;
}

/** A part of the day: its name, the price once, then the tiles. */
function daypartGroup(key: Slot["daypart"], label: string, g: Slot[]) {
  const prices = [...new Set(g.map((s) => s.price))].sort((a, b) => a - b);
  const price = prices.length === 1 ? rp(prices[0]) : `${rp(prices[0])} to ${rp(prices[prices.length - 1])}`;
  const open = g.filter((s) => s.courtsFree > 0).length;
  return `<div class="grp" role="group" aria-label="${label}, ${open} of ${g.length} times open, ${price} per court">
    <div class="grp-top">
      <h3 class="grp-h" id="grp-${key}">${label}</h3>
      <p class="grp-p" aria-hidden="true"><span class="num grp-price" data-v="${prices[0]}">${price}</span><span class="grp-u">per court</span></p>
    </div>
    <div class="tiles">${g.map(slotTile).join("")}</div>
  </div>`;
}

function renderTimes() {
  $("times-h").textContent = `Times on ${dateLabel(S.date)}`;
  timesEl.setAttribute("aria-busy", String(S.load === "loading"));
  if (S.load === "loading") {
    timesEl.innerHTML = `<div class="grp" aria-hidden="true"><div class="grp-top"><span class="sk sk-h"></span></div><div class="tiles">${'<span class="sk sk-tile"></span>'.repeat(8)}</div></div><p class="sr-only">Loading times.</p>`;
    return;
  }
  if (S.load === "error") {
    timesEl.innerHTML = `<div class="state">
      ${Icon("alert", "ic ic-danger")}
      <div class="state-tx"><p class="body-strong">Times did not load.</p><p class="body ink-muted">Check your connection, then try again.</p></div>
      <button type="button" class="btn btn-text" data-retry>${Icon("refresh")}Try again</button>
    </div>`;
    rise(timesEl.querySelectorAll(".state"));
    return;
  }
  const slots = S.avail?.slots ?? [];
  const open = slots.filter((s) => s.courtsFree > 0);
  if (!open.length) {
    const i = S.days.indexOf(S.date);
    const nextDay = S.days[i + 1];
    const today = i === 0;
    timesEl.innerHTML = `<div class="state">
      ${Icon("calendar", "ic ic-muted")}
      <div class="state-tx"><p class="body-strong">${today && !slots.length ? "No more times today." : "Fully booked."}</p><p class="body ink-muted">Every court is taken on ${dateLabel(S.date)} for ${S.duration} minutes.</p></div>
      ${nextDay ? `<button type="button" class="btn btn-text" data-next="${nextDay}">See ${i === 0 ? "tomorrow" : dateLabel(nextDay)}</button>` : ""}
    </div>`;
    rise(timesEl.querySelectorAll(".state"));
    return;
  }
  timesEl.innerHTML = DAYPARTS.map(([k, label]) => {
    const g = slots.filter((s) => s.daypart === k);
    return g.length ? daypartGroup(k, label, g) : "";
  }).join("");
  rise(timesEl.querySelectorAll(".grp-top, .slot"), 0.012, 12);
  if (M) timesEl.querySelectorAll<HTMLElement>(".grp-price[data-v]").forEach((el, i) => {
    if (el.textContent?.includes(" to ")) return;
    const o = { v: 0 };
    const to = Number(el.dataset.v);
    gsap.to(o, { v: to, duration: 0.9, delay: 0.1 + i * 0.03, ease: "power3.out", onUpdate: () => void (el.textContent = rp(Math.round(o.v / 1000) * 1000)), onComplete: () => void (el.textContent = rp(to)) });
  });
}

timesEl.addEventListener("click", (e) => {
  const t = e.target as HTMLElement;
  if (t.closest("[data-retry]")) return void load(true);
  const n = t.closest<HTMLElement>("[data-next]");
  if (n) return void setDate(n.dataset.next!);
  const b = t.closest<HTMLButtonElement>(".slot[data-time]");
  if (!b || b.disabled) return;
  chooseTime(b.dataset.time!);
});

function chooseTime(time: string) {
  const changed = time !== S.time;
  S.time = time;
  if (changed) S.court = null;
  const prev = timesEl.querySelector(".slot.is-sel");
  let row: HTMLElement | null = null;
  timesEl.querySelectorAll<HTMLButtonElement>(".slot[data-time]").forEach((r) => {
    const on = r.dataset.time === time;
    r.classList.toggle("is-sel", on);
    r.setAttribute("aria-pressed", String(on));
    if (on) row = r;
  });
  slideFill(prev, row);
  syncUrl();
  updateSide();
  updateBar();
  // desktop: the jewel plan is on screen, so the court is picked there; a ball arc links the row to the plan
  if (jewelOn()) {
    if (changed && row) ballArc(row);
  } else openSheet();
}

/* preview on the plan: the lit courts follow the hovered or focused time, then return to the chosen one */
let hoverT: string | null = null;
function preview(b: HTMLButtonElement | null) {
  const t = b && !b.disabled ? b.dataset.time! : null;
  if (t === hoverT) return;
  hoverT = t;
  updateSide(t ?? undefined);
}
timesEl.addEventListener("pointerover", (e) => {
  if (e.pointerType !== "mouse") return;
  preview((e.target as HTMLElement).closest<HTMLButtonElement>(".slot[data-time]"));
});
timesEl.addEventListener("pointerleave", () => preview(null));
timesEl.addEventListener("focusin", (e) => preview((e.target as HTMLElement).closest<HTMLButtonElement>(".slot[data-time]")));
timesEl.addEventListener("focusout", (e) => {
  if (!timesEl.contains(e.relatedTarget as Node | null)) preview(null);
});

/* ---------- desktop side plan ---------- */
let sideKey = "";
const sidePlan = new CourtPlan($("side-plan"), { motion: M });
sidePlan.intro();
const jewelOn = () => $("side-plan").offsetParent !== null;
const miniPlan = new CourtPlan($("mini-plan"), { motion: M });
miniPlan.intro();
$("mini-go").addEventListener("click", () => {
  if (S.time && sel()) return openSheet();
  const next = S.avail?.slots.find((x) => x.courtsFree > 0);
  if (next) chooseTime(next.time);
});
sidePlan.el.addEventListener("click", (e) => {
  const s = sel();
  const id = sidePlan.courtAt(e.clientX, e.clientY);
  if (!s || !id || !s.courts.some((c) => c.id === id && c.free)) return;
  S.court = id;
  updateSide();
  updateBar();
});
sidePlan.el.addEventListener("pointermove", (e) => {
  const s = sel();
  const id = sidePlan.courtAt(e.clientX, e.clientY);
  const hit = s && id && s.courts.some((c) => c.id === id && c.free) ? id : null;
  sidePlan.el.style.cursor = hit ? "pointer" : "default";
  sidePlan.hover(hit && hit !== S.court ? hit : null);
});
sidePlan.el.addEventListener("pointerleave", () => sidePlan.hover(null));

/** A thin ball arc traced once from the chosen row to the plan, the ball riding its head. */
function ballArc(row: HTMLElement) {
  if (!M) return;
  const s = sel();
  const target = s?.courts.find((c) => c.free)?.id ?? 1;
  const r = row.getBoundingClientRect();
  const a = { x: r.right - 8, y: r.top + r.height / 2 };
  const b = sidePlan.courtPoint(target);
  const lift = Math.max(80, Math.abs(b.x - a.x) * 0.45);
  const c = { x: (a.x + b.x) / 2, y: Math.min(a.y, b.y) - lift };
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("class", "arc-layer");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", `M${a.x} ${a.y}Q${c.x} ${c.y} ${b.x} ${b.y}`);
  const ball = document.createElementNS(ns, "circle");
  ball.setAttribute("r", "5");
  svg.append(path, ball);
  document.body.appendChild(svg);
  const len = path.getTotalLength();
  path.style.strokeDasharray = `${len}`;
  const o = { p: 0 };
  gsap.timeline({ onComplete: () => svg.remove() })
    .to(o, {
      p: 1, duration: 0.8, ease: "power2.inOut",
      onUpdate: () => {
        path.style.strokeDashoffset = `${len * (1 - o.p)}`;
        const pt = path.getPointAtLength(len * o.p);
        ball.setAttribute("cx", String(pt.x));
        ball.setAttribute("cy", String(pt.y));
      },
    })
    .to(svg, { opacity: 0, duration: 0.4, ease: "power1.out" }, "+=0.15");
}
function updateSide(previewTime?: string) {
  const chosen = sel();
  const s = previewTime ? slotOf(previewTime) : chosen ?? S.avail?.slots.find((x) => x.courtsFree > 0) ?? null;
  const sub = $("side-sub");
  const key = s ? `${S.date}|${S.duration}|${s.time}|${S.court}|${!!chosen}` : `none|${S.load}`;
  if (key === sideKey) return;
  sideKey = key;
  if (!s) {
    sub.textContent = S.load === "loading" ? "Checking the courts." : "Pick a time to see which courts are free.";
    sidePlan.set(null, null, "The eight courts under the dome.");
    miniPlan.set(null, null, "The eight courts under the dome.");
    $("mini-sub").textContent = sub.textContent;
    $<HTMLButtonElement>("mini-go").disabled = true;
    return;
  }
  const lead = previewTime || chosen ? `At ${s.time}` : `Next free at ${s.time}`;
  sub.textContent = `${lead}, ${ofEight(s.courtsFree)}.`;
  $("mini-sub").textContent = sub.textContent;
  $<HTMLButtonElement>("mini-go").disabled = false;

  miniPlan.set(s.courts, s.time === S.time ? S.court : null, `${ofEight(s.courtsFree)} at ${s.time}.`);
  sidePlan.set(
    s.courts,
    s.time === S.time ? S.court : null,
    `${ofEight(s.courtsFree)} at ${s.time}. Free: ${s.courts.filter((c) => c.free).map((c) => c.id).join(", ") || "none"}.`,
  );
}

/** "1 of 8 courts is free", "3 of 8 courts are free" */
function ofEight(n: number) { return `${n} of 8 courts ${n === 1 ? "is" : "are"} free`; }

/* ---------- court sheet ---------- */
const sheet = $<HTMLDialogElement>("sheet");
const panel = $("sheet-panel");
const scrim = $("sheet-scrim");
let sheetBusy = false;

let sheetPlan: CourtPlan | null = null;
/** The court plan in the sheet: a free court picks on click, hover marks it. */
function sheetCourt(e: PointerEvent | MouseEvent) {
  const s = sel();
  const id = sheetPlan?.courtAt(e.clientX, e.clientY) ?? null;
  return s && id && s.courts.some((c) => c.id === id && c.free) ? id : null;
}
function paintSheet() {
  const s = sel();
  if (!s) return;
  $("sheet-h").textContent = `Courts at ${s.time}`;
  $("sheet-sub").innerHTML = `${dateLabel(S.date)}, ${S.duration} min, <span class="nowrap">${rp(s.price)}</span> per court.`;
  sheetPlan ??= new CourtPlan($("sheet-plan"), { motion: M });
  sheetPlan.readColors();
  sheetPlan.set(s.courts, S.court, `Plan of the dome: ${ofEight(s.courtsFree)} at ${s.time}.`);
  $("sheet-rows").innerHTML = s.courts
    .filter((c) => c.free)
    .map(
      (c) => `<button type="button" class="crow${c.id === S.court ? " is-sel" : ""}" data-court="${c.id}" aria-pressed="${c.id === S.court}">
        <span class="body-strong num">Court ${c.id}</span>
        <span class="crow-free caption">${Icon("check", "ic ic-court")}Free</span>
        ${Icon("chevron", "ic slot-chev")}
      </button>`,
    )
    .join("");
}

function openSheet() {
  if (!sel() || sheet.open) return;
  paintSheet();
  sheet.showModal();
  document.documentElement.classList.add("bk-locked");
  const first = sheet.querySelector<HTMLElement>(".crow.is-sel") ?? sheet.querySelector<HTMLElement>(".crow");
  first?.focus({ preventScroll: true });
  if (M) {
    sheetBusy = true;
    gsap.fromTo(scrim, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power1.out" });
    gsap.fromTo(panel, { yPercent: 100 }, { yPercent: 0, duration: 0.5, ease: IN, onComplete: () => void (sheetBusy = false) });
    gsap.fromTo(sheet.querySelectorAll(".crow"), { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.5, ease: IN, stagger: 0.03, delay: 0.16, clearProps: "transform,opacity" });
  }
}

function closeSheet(then?: () => void) {
  if (!sheet.open) return then?.();
  const done = () => {
    sheet.close();
    document.documentElement.classList.remove("bk-locked");
    gsap.set([panel, scrim], { clearProps: "transform,opacity" });
    sheetBusy = false;
    if (then) then();
    else timesEl.querySelector<HTMLElement>(`.slot[data-time="${S.time}"]`)?.focus({ preventScroll: true });
  };
  if (!M) return done();
  sheetBusy = true;
  gsap.to(scrim, { opacity: 0, duration: 0.21, ease: "power1.in" });
  gsap.to(panel, { yPercent: 100, duration: 0.28, ease: "power2.in", onComplete: done });
}

sheet.addEventListener("cancel", (e) => {
  e.preventDefault();
  if (!sheetBusy) closeSheet();
});
scrim.addEventListener("click", () => !sheetBusy && closeSheet());
$("sheet-x").addEventListener("click", () => closeSheet());
sheet.addEventListener("click", (e) => {
  if (sheetPlan && e.target === sheetPlan.el) {
    const id = sheetCourt(e);
    if (id) pickCourt(id);
    return;
  }
  const c = (e.target as Element).closest<HTMLElement>("[data-court]");
  if (c) pickCourt(Number(c.getAttribute("data-court")));
});
$("sheet-plan").addEventListener("pointermove", (e) => {
  if (!sheetPlan) return;
  const id = sheetCourt(e);
  sheetPlan.el.style.cursor = id ? "pointer" : "default";
  sheetPlan.hover(id && id !== S.court ? id : null);
});
$("sheet-plan").addEventListener("pointerleave", () => sheetPlan?.hover(null));

function pickCourt(id: number) {
  if (sheetBusy && M) return;
  S.court = id;
  sheet.querySelectorAll<HTMLElement>(".crow").forEach((r) => {
    const on = Number(r.dataset.court) === id;
    r.classList.toggle("is-sel", on);
    r.setAttribute("aria-pressed", String(on));
  });
  sheetPlan?.hover(null);
  sheetPlan?.select(id);
  updateSide();
  updateBar();
  setTimeout(() => closeSheet(() => visibleGo()?.focus({ preventScroll: true })), M ? 280 : 0);
}

/* ---------- summary bar ---------- */
const bar = $("bar");
new ResizeObserver(() => {
  if (bar.offsetHeight) document.documentElement.style.setProperty("--bar-h", `${bar.offsetHeight}px`);
}).observe(bar);
let barShown = false;
/** Phone and tablet: the bar shows from the moment the time step comes into view, before a time is picked. */
let reachedTime = false;
new IntersectionObserver((es) => {
  if (reachedTime || !es.some((e) => e.isIntersecting || e.boundingClientRect.top < 0)) return;
  reachedTime = true;
  updateBar();
}).observe($("times-h").closest(".blk")!);
/** The bar belongs to the booking steps: the moment the steps band's foot reaches the bar's lower edge (where the
 *  footer would slide in under it) the bar is released, so it never covers the footer. Checked on every scroll and
 *  resize, synchronously, so even an instant jump to the footer never paints the bar over it. */
let inWork = true;
const workEl = $("work");
function checkWork() {
  const tab = document.querySelector<HTMLElement>(".ch-tabbar");
  const tr = tab && getComputedStyle(tab).display !== "none" ? tab.getBoundingClientRect() : null;
  const tabTop = tr && tr.height ? tr.top : window.innerHeight;
  const v = workEl.getBoundingClientRect().bottom > Math.min(window.innerHeight, tabTop) + 1;
  if (v === inWork) return;
  inWork = v;
  updateBar();
}
addEventListener("scroll", checkWork, { passive: true });
addEventListener("resize", checkWork);
new ResizeObserver(checkWork).observe(workEl);
function lineFor() {
  const parts = [dateLabel(S.date), S.time ?? "", `${S.duration} min`];
  if (S.court) parts.unshift(`Court ${S.court}`);
  return parts.join(", ");
}
function heads() {
  return 1 + playerNames().length;
}
const GO = () => Array.from(document.querySelectorAll<HTMLButtonElement>("[data-go]"));
const goLabels = () => GO().map((b) => b.querySelector<HTMLElement>(".go-t")!);
/** The primary that is on screen: the jewel card from 1024px, the bar below. */
function visibleGo() {
  return GO().find((b) => b.offsetParent !== null && !b.closest("[hidden]")) ?? null;
}
function setGo(text: string, price?: number) {
  goLabels().forEach((t) => {
    if (price === undefined) {
      delete t.dataset.v;
      t.textContent = text;
    } else tick(t, price, (n) => `${text} ${rp(n)}`);
  });
}
function updateBar() {
  const s = sel();
  const show = inWork && ((S.step === "pick" && (!!s || reachedTime)) || S.step === "checkout");
  // jewel summary (desktop) on the pick step
  if (S.step === "pick") {
    const jt = $("jw-time");
    const jg = $<HTMLButtonElement>("jw-go");
    if (s) {
      jt.textContent = `${s.time} to ${endTime(s.time, S.duration)}`;
      jt.classList.remove("is-empty");
      $("jw-meta").textContent = `${dateLabel(S.date)}, ${S.duration} min${S.court ? `, Court ${S.court}` : ", court not chosen"}`;
      $("jw-price-l").textContent = "Price";
      tick($("jw-price"), s.price, rp);
      jg.disabled = false;
    } else {
      jt.textContent = "No time chosen";
      jt.classList.add("is-empty");
      $("jw-meta").textContent = `${dateLabel(S.date)}, ${S.duration} min. Pick a time.`;
      $("jw-price-l").textContent = `From, ${S.duration} min`;
      tick($("jw-price"), (700000 * S.duration) / 60, rp);
      jg.disabled = true;
    }
  }
  if (S.step === "pick") {
    setGo(!s ? "Choose a time" : S.court ? "Continue" : "Choose a court");
    // the bar names the next move plainly: disabled "Choose a time" until a slot is picked, then "Continue"
    const bg = $<HTMLButtonElement>("bar-go");
    bg.disabled = !s;
    bg.querySelector(".go-t")!.textContent = s ? "Continue" : "Choose a time";
  } else $<HTMLButtonElement>("bar-go").disabled = false;
  if (show && !s && S.step === "pick") {
    $("bar-line").textContent = `${dateLabel(S.date)}, ${S.duration} min`;
    tick($("bar-price"), S.load === "ok" && S.avail?.slots[0] ? S.avail.slots[0].price : (700000 * S.duration) / 60, rp);
    $("bar-unit").textContent = "per court";
  }
  if (show && s) {
    $("bar-line").textContent = lineFor();
    const share = Math.ceil(s.price / heads());
    if (S.step === "checkout") {
      tick($("bar-price"), S.split ? share : s.price, rp);
      $("bar-unit").textContent = S.split ? `each, ${heads()} players` : "for the court";
      if (!S.submitting) setGo("Pay", s.price);
      $("sum-total-l").textContent = S.split ? `Each of ${heads()}` : "Total";
      tick($("sum-total"), S.split ? share : s.price, rp);
    } else {
      tick($("bar-price"), s.price, rp);
      $("bar-unit").textContent = "per court";
    }
  }
  document.documentElement.classList.toggle("bk-bar-on", show);
  if (show && (!barShown || bar.hidden)) {
    barShown = true;
    gsap.killTweensOf(bar);
    bar.hidden = false;
    if (M) gsap.fromTo(bar, { yPercent: 100 }, { yPercent: 0, duration: 0.5, ease: IN, clearProps: "transform" });
  } else if (!show && barShown) {
    barShown = false;
    // released at the footer: gone at once, so not even a slide-out passes over the footer
    if (!M || !inWork) { gsap.killTweensOf(bar); gsap.set(bar, { clearProps: "transform" }); bar.hidden = true; }
    else
      gsap.to(bar, {
        yPercent: 100,
        duration: 0.25,
        ease: "power2.in",
        onComplete: () => {
          if (!barShown) bar.hidden = true;
          gsap.set(bar, { clearProps: "transform" });
        },
      });
  }
}
document.addEventListener("click", (e) => {
  if (!(e.target as HTMLElement).closest("[data-go]")) return;
  if (S.step === "pick") {
    if (S.court) go("checkout");
    else openSheet();
  } else if (S.step === "checkout") $<HTMLFormElement>("co-form").requestSubmit();
});

/* ---------- steps ---------- */
const STEP_EL: Record<Step, string> = { pick: "step-pick", checkout: "step-checkout", done: "step-done", mine: "step-mine" };
let moving: Promise<void> = Promise.resolve();

function go(step: Step, opts: { push?: boolean } = {}) {
  moving = moving.then(() => transition(step, opts.push !== false));
  return moving;
}

async function transition(step: Step, push: boolean) {
  if (step === S.step) return;
  const from = $(STEP_EL[S.step]);
  const to = $(STEP_EL[step]);
  const head = $("bk-head");
  const headOn = step === "pick" || step === "mine";
  if (M) {
    const outs: Element[] = [step === "done" ? $("work") : from];
    if (!headOn && !head.hidden) outs.push(head);
    await gsap.to(outs, { opacity: 0, y: -8, duration: 0.21, ease: "power2.in" });
    gsap.set(outs, { clearProps: "transform,opacity" });
  }
  const prev = S.step;
  S.step = step;
  from.hidden = true;
  const headWas = head.hidden;
  head.hidden = !headOn;
  $("work").hidden = step === "done";
  to.hidden = false;
  paintView();
  if (step === "checkout") renderCheckout();
  if (step === "done") renderDone();
  if (step === "mine") enterMine();
  if (step === "pick" && prev === "done") load();
  updateBar();
  window.scrollTo({ top: 0, behavior: "auto" });
  if (push && (step === "checkout" || step === "mine") && prev !== step) history.pushState({ step }, "", step === "mine" ? `${location.pathname}${location.search}#my-bookings` : location.href.split("#")[0]);
  if (step === "pick" && location.hash) history.replaceState(null, "", location.pathname + location.search);
  const focusId = step === "checkout" ? "co-h" : step === "done" ? "done-h" : step === "mine" ? "mine-h" : "";
  if (focusId) $(focusId).focus({ preventScroll: true });
  if (step === "done") return playSeal();
  const ins: Element[] = [];
  if (headOn && headWas) ins.push(...Array.from(head.querySelectorAll(".head-top, .head-low, .head-photo")));
  ins.push(...Array.from(to.querySelectorAll(":scope > .back, .split-main > *, .jewel, .mine > *")));
  rise(ins, 0.05, 24);
}

window.addEventListener("popstate", () => {
  const want: Step = location.hash === "#my-bookings" ? "mine" : (history.state?.step as Step) === "checkout" && S.court ? "checkout" : "pick";
  if (want !== S.step && !(S.step === "done" && want === "checkout")) go(want, { push: false });
});

/* view switch */
const viewSeg = $("view-seg");
function paintView() {
  pressIn(viewSeg, "[data-view]", (b) => (b.dataset.view === "mine") === (S.step === "mine"));
}
viewSeg.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-view]");
  if (!b) return;
  if (b.dataset.view === "mine" && S.step !== "mine") go("mine");
  if (b.dataset.view === "book" && S.step === "mine") go("pick");
});
$("co-back").innerHTML = `${Icon("back")}Back to times`;
$("co-back").addEventListener("click", () => go("pick"));

/* ---------- checkout ---------- */
const form = $<HTMLFormElement>("co-form");
const playersEl = $("players");
const addBtn = $<HTMLButtonElement>("add-player");
let players: string[] = [];

const HELP: Record<string, string> = {
  name: "",
  phone: "Starts with 08 or +62.",
  email: "For the receipt.",
  lookup: "Starts with 08 or +62.",
};

function setErr(key: string, msg: string | null) {
  const f = document.querySelector<HTMLElement>(`.field[data-f="${key}"]`);
  if (!f) return;
  const input = f.querySelector<HTMLInputElement>("input")!;
  const help = f.querySelector<HTMLElement>(".fhelp")!;
  f.classList.toggle("is-err", !!msg);
  input.setAttribute("aria-invalid", String(!!msg));
  help.innerHTML = msg ? `${Icon("alert", "ic ic-sm")}<span>${esc(msg)}</span>` : esc(HELP[key] ?? "");
}

function playerNames() {
  return players.map((p) => p.trim()).filter(Boolean);
}

function renderPlayers(focusLast = false) {
  playersEl.innerHTML = players
    .map(
      (p, i) => `<div class="prow">
        <div class="field" data-f="p${i}">
          <label class="flabel" for="f-p${i}">Player ${i + 2}</label>
          <input class="fcontrol" id="f-p${i}" data-p="${i}" maxlength="60" autocomplete="off" value="${esc(p)}" aria-describedby="h-p${i}">
          <p class="fhelp caption" id="h-p${i}"></p>
        </div>
        <button type="button" class="btn btn-tile btn-ic prm" data-rm="${i}" aria-label="Remove player ${i + 2}">${Icon("close")}</button>
      </div>`,
    )
    .join("");
  const full = players.length >= 3;
  addBtn.disabled = full;
  addBtn.innerHTML = full ? "Three players added" : `${Icon("plus")}Add a player`;
  if (focusLast) {
    const inputs = playersEl.querySelectorAll<HTMLInputElement>("input");
    inputs[inputs.length - 1]?.focus();
    if (M) rise([playersEl.lastElementChild!], 0, 12);
  }
}
addBtn.addEventListener("click", () => {
  if (players.length >= 3) return;
  players.push("");
  renderPlayers(true);
  paintSplit();
});
playersEl.addEventListener("input", (e) => {
  const t = e.target as HTMLInputElement;
  if (t.dataset.p) {
    players[Number(t.dataset.p)] = t.value;
    paintSplit();
  }
});
playersEl.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>("[data-rm]");
  if (!b) return;
  players.splice(Number(b.dataset.rm), 1);
  renderPlayers();
  (playersEl.querySelector<HTMLElement>("[data-rm]") ?? addBtn).focus();
  paintSplit();
});

const paySeg = form.querySelector<HTMLElement>(".seg-pay")!;
paySeg.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-split]");
  if (!b) return;
  S.split = b.dataset.split === "1";
  paintSplit();
});
let splitErr = false;
function paintSplit() {
  pressIn(paySeg, "[data-split]", (b) => (b.dataset.split === "1") === S.split);
  const s = sel();
  const line = $("split-line");
  if (!s) return;
  const n = heads();
  if (S.split && n < 2) {
    line.classList.toggle("is-err", splitErr);
    line.innerHTML = splitErr
      ? `${Icon("alert", "ic ic-sm")}<span>Add at least one player to split the court.</span>`
      : "Add a player to split the court.";
  } else {
    splitErr = false;
    line.classList.remove("is-err");
    line.textContent = S.split
      ? `${rp(Math.ceil(s.price / n))} each for ${n} players. Unpaid shares fall back to you.`
      : `${rp(s.price)} for the court, paid by you.`;
  }
  updateBar();
}

function validate(): Record<string, string> {
  const errs: Record<string, string> = {};
  const name = $<HTMLInputElement>("f-name").value.trim();
  const phone = $<HTMLInputElement>("f-phone").value.trim();
  const email = $<HTMLInputElement>("f-email").value.trim();
  if (name.length < 2) errs.name = "Enter your name.";
  if (!phone) errs.phone = "Enter your phone number.";
  else if (!phoneOk(phone)) errs.phone = "Use 08 or +62, then 8 to 13 digits.";
  if (email && !emailOk(email)) errs.email = "Check the email address.";
  return errs;
}
// inline validation: on leaving a field, and live once a field has shown an error
(["name", "phone", "email"] as const).forEach((k) => {
  const el = $<HTMLInputElement>(`f-${k}`);
  el.addEventListener("blur", () => {
    if (!el.value.trim() && k !== "phone") return setErr(k, null);
    if (!el.value.trim()) return;
    setErr(k, validate()[k] ?? null);
  });
  el.addEventListener("input", () => {
    if (el.closest(".field")!.classList.contains("is-err")) setErr(k, validate()[k] ?? null);
  });
});

function notice(kind: "danger" | "warning", title: string, body: string, action?: { id: string; label: string }) {
  const n = $("co-notice");
  n.hidden = false;
  n.innerHTML = `<div class="notice notice-${kind}" role="alert">
    ${Icon("alert", `ic ic-${kind}`)}
    <div class="notice-tx"><p class="body-strong">${esc(title)}</p><p class="body">${esc(body)}</p>
    ${action ? `<button type="button" class="btn btn-text notice-act" id="${action.id}">${esc(action.label)}</button>` : ""}</div>
  </div>`;
  rise([n], 0, 8);
  n.scrollIntoView({ block: "center", behavior: M ? "smooth" : "auto" });
}

let sumPlan: CourtPlan | null = null;
let passPlan: CourtPlan | null = null;
function renderCheckout() {
  const s = sel();
  if (!s || !S.court) return;
  const terms = S.avail?.monthly?.terms ?? [];
  $("monthly-terms").innerHTML = terms.map((t) => `<li>${esc(t.replace(/\.?$/, "."))}</li>`).join("");
  $("monthly").hidden = !terms.length;
  document.querySelectorAll<HTMLElement>(".disc-ic").forEach((el) => (el.innerHTML = Icon("chevron", "ic ic-sm")));
  $("co-notice").hidden = true;
  const remembered = store.get(PHONE_KEY);
  const ph = $<HTMLInputElement>("f-phone");
  if (!ph.value && remembered) ph.value = remembered;
  renderPlayers();
  paintSplit();
  sumPlan ??= new CourtPlan($("sum-plan"), { motion: M });
  sumPlan.set(s.courts.map((c) => ({ id: c.id, free: false })), null, `Court ${S.court} under the dome.`);
  sumPlan.intro();
  const chosen = S.court;
  setTimeout(() => sumPlan!.set(s.courts.map((c) => ({ id: c.id, free: c.id === chosen })), chosen, `Court ${chosen} under the dome.`), M ? 500 : 0);
  $("sum-time").textContent = `${s.time} to ${endTime(s.time, S.duration)}`;
  $("sum-day").textContent = `${dateLabel(S.date)}, Court ${S.court}, ${S.duration} min`;
  tick($("sum-total"), s.price, rp);
}

function dl(rows: [string, string][]) {
  return rows.map(([k, v]) => `<div class="dl-row"><dt class="body ink-muted">${k}</dt><dd class="body-strong">${esc(v)}</dd></div>`).join("");
}

form.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (S.submitting) return;
  const s = sel();
  if (!s || !S.court) return void go("pick");
  const errs = validate();
  (["name", "phone", "email"] as const).forEach((k) => setErr(k, errs[k] ?? null));
  splitErr = S.split && heads() < 2;
  paintSplit();
  const firstBad = (["name", "phone", "email"] as const).find((k) => errs[k]);
  if (firstBad || splitErr) {
    const el = firstBad ? $(`f-${firstBad}`) : paySeg.querySelector<HTMLElement>('[data-split="1"]')!;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: "center", behavior: M ? "smooth" : "auto" });
    return;
  }
  const email = $<HTMLInputElement>("f-email").value.trim();
  const body = {
    date: S.date,
    time: s.time,
    duration: S.duration,
    court: S.court,
    name: $<HTMLInputElement>("f-name").value.trim(),
    phone: cleanPhone($<HTMLInputElement>("f-phone").value.trim()),
    ...(email ? { email } : {}),
    players: playerNames(),
    split: S.split,
    method: (new FormData(form).get("method") as Method) ?? "card",
  };
  setPaying(true);
  $("co-notice").hidden = true;
  try {
    const res = await api.book(body);
    S.booking = res.booking;
    store.set(PHONE_KEY, body.phone);
    setPaying(false);
    await go("done", { push: false });
    history.replaceState({ step: "done" }, "", location.pathname + location.search);
  } catch (err) {
    setPaying(false);
    const a = err instanceof ApiError ? err : new ApiError(0, "unknown", "Something went wrong.");
    if (a.status === 409) {
      const court = S.court;
      notice("danger", `Court ${court} was just taken. Pick another court.`, "Someone booked it a moment ago. Nothing was charged.", { id: "n-repick", label: "Pick another court" });
      $("n-repick").addEventListener("click", repick);
    } else if (a.status === 400 && a.fields) {
      const known = ["name", "phone", "email"];
      Object.entries(a.fields).forEach(([k, v]) => known.includes(k) && setErr(k, v.endsWith(".") ? v : v + "."));
      const other = Object.entries(a.fields).filter(([k]) => !known.includes(k));
      if (other.length) notice("danger", "Check the booking.", other.map(([, v]) => v).join(". ") + ".", { id: "n-repick", label: "Back to times" });
      else notice("danger", "Check the highlighted details.", "Fix them, then pay again.");
      document.getElementById("n-repick")?.addEventListener("click", () => go("pick"));
      const k = known.find((x) => a.fields![x]);
      if (k) $(`f-${k}`).focus({ preventScroll: true });
    } else if (a.status === 429) {
      notice("warning", "Too many attempts.", "Wait a few minutes, then try again.");
    } else if (a.status === 0) {
      notice("danger", "Payment did not go through.", "The connection dropped. Nothing was charged. Try again.");
    } else {
      notice("danger", "Payment did not go through.", `${a.message.replace(/\.?$/, ".")} Nothing was charged.`);
    }
  }
});

function setPaying(on: boolean) {
  S.submitting = on;
  GO().forEach((b) => {
    b.setAttribute("aria-busy", String(on));
    b.classList.toggle("is-loading", on);
  });
  goLabels().forEach((t) => {
    if (on) {
      t.dataset.label = t.textContent ?? "";
      t.innerHTML = `<span class="spin" aria-hidden="true"></span>Paying`;
    } else if (t.dataset.label) t.textContent = t.dataset.label;
  });
  form.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input, button").forEach((x) => (x.disabled = on || (x === addBtn && players.length >= 3)));
}

async function repick() {
  const t = S.time;
  S.court = null;
  await go("pick");
  await load(true);
  if (t && sel()?.time === t) openSheet();
}

/* ---------- confirmed ---------- */
function renderDone() {
  const b = S.booking;
  if (!b) return;
  $("seal-num").textContent = String(b.court);
  $("done-ref").textContent = `Ref ${b.ref}`;
  const methods: Record<Method, string> = { card: "Card", gopay: "GoPay", va: "Bank virtual account" };
  const who = ["You", ...b.players].join(", ");
  $("done-pass").innerHTML = `
    <div class="pass-top">
      <div class="pass-plan" id="pass-plan"></div>
      <div class="pass-main">
        <p class="body ink-muted num">${dateLabel(b.date)}</p>
        <p class="h2 num pass-time">${b.time} to ${endTime(b.time, b.duration)}</p>
        <p class="body-strong num">Court ${b.court}, ${b.duration} min</p>
        <p class="body ink-muted">${esc(who)}</p>
      </div>
    </div>
    <div class="pass-foot">
      <dl class="dl num">${dl([
        ["Payment", b.split ? `Split, ${rp(b.share ?? Math.ceil(b.total / (1 + b.players.length)))} each` : "Full court"],
        ["Method", `${methods[b.method]}, demo, no charge`],
        ["Reference", b.ref],
      ])}</dl>
      <div class="pass-total"><span class="body ink-muted">Total</span><span class="title num">${rp(b.total)}</span></div>
    </div>`;
  passPlan?.destroy();
  passPlan = new CourtPlan($("pass-plan"), { motion: M });
  // the pass waits hidden for the seal; then the hall fades in and the booked court lights
  if (M) passPlan.state.reveal = 0;
  passPlan.set(Array.from({ length: 8 }, (_, i) => ({ id: i + 1, free: i + 1 === b.court })), null, `Court ${b.court} lit on the plan of the dome.`);
  $("act-ics").innerHTML = `${Icon("calendar")}Add to calendar`;
  const map = $<HTMLAnchorElement>("act-map");
  map.innerHTML = `${Icon("pin")}Directions`;
  map.href = MAPS;
}
$("act-ics").addEventListener("click", () => S.booking && downloadIcs(S.booking));
$("act-again").addEventListener("click", () => {
  S.time = null;
  S.court = null;
  S.booking = null;
  S.split = false;
  players = [];
  go("pick");
});

function playSeal() {
  const ring = document.querySelector<SVGCircleElement>(".seal-c")!;
  const dot = document.querySelector<SVGCircleElement>(".seal-dot")!;
  const num = $("seal").querySelector(".seal-in")!;
  const rest = Array.from(document.querySelectorAll("#step-done .done > :not(.seal)"));
  if (M) setTimeout(() => passPlan?.intro(), 1300);
  if (M && S.booking) { const id = S.booking.court; setTimeout(() => passPlan?.pulse(id), 2300); }
  if (!M) {
    gsap.set(ring, { strokeDashoffset: 0 });
    gsap.set(dot, { opacity: 1 });
    return;
  }
  const tl = gsap.timeline();
  tl.fromTo(ring, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 1.2, ease: "power2.inOut" })
    .fromTo(num, { opacity: 0, scale: 0.92 }, { opacity: 1, scale: 1, duration: 0.8, ease: IN }, 0.3)
    .fromTo(dot, { opacity: 0, y: -14 }, { opacity: 1, y: 0, duration: 0.45, ease: "back.out(2.4)", transformOrigin: "50% 50%" }, 1.15)
    .fromTo(rest, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.6, ease: IN, stagger: 0.08, clearProps: "transform,opacity" }, 1.25);
}

/* ---------- my bookings ---------- */
const mineForm = $<HTMLFormElement>("mine-form");
const mineList = $("mine-list");
let minePhone = "";
let mineRows: Booking[] = [];

function enterMine() {
  const remembered = store.get(PHONE_KEY);
  const f = $<HTMLInputElement>("f-lookup");
  if (!f.value && remembered) f.value = remembered;
  if (f.value && phoneOk(f.value) && cleanPhone(f.value) !== minePhone) lookup(cleanPhone(f.value));
}

mineForm.addEventListener("submit", (e) => {
  e.preventDefault();
  const v = $<HTMLInputElement>("f-lookup").value.trim();
  if (!v) return setErr("lookup", "Enter your phone number."), $("f-lookup").focus();
  if (!phoneOk(v)) return setErr("lookup", "Use 08 or +62, then 8 to 13 digits."), $("f-lookup").focus();
  setErr("lookup", null);
  lookup(cleanPhone(v));
});
$("f-lookup").addEventListener("input", () => {
  const f = $("f-lookup").closest(".field")!;
  if (f.classList.contains("is-err") && phoneOk(($("f-lookup") as HTMLInputElement).value)) setErr("lookup", null);
});

async function lookup(phone: string) {
  minePhone = phone;
  store.set(PHONE_KEY, phone);
  const go = $<HTMLButtonElement>("mine-go");
  go.setAttribute("aria-busy", "true");
  go.innerHTML = `<span class="spin" aria-hidden="true"></span>Finding`;
  mineList.innerHTML = `<div class="list" aria-hidden="true">${'<div class="brow sk-row"><span class="sk sk-a"></span><span class="sk sk-b"></span></div>'.repeat(3)}</div>`;
  try {
    const res = await api.list(phone);
    mineRows = res.bookings;
    renderMine();
  } catch (e) {
    const a = e as ApiError;
    mineList.innerHTML = `<div class="state">${Icon("alert", "ic ic-danger")}
      <div class="state-tx"><p class="body-strong">Bookings did not load.</p><p class="body ink-muted">${a.status === 429 ? "Too many lookups. Wait a few minutes." : "Check your connection, then try again."}</p></div>
      <button type="button" class="btn btn-text" data-relook>${Icon("refresh")}Try again</button></div>`;
  } finally {
    go.removeAttribute("aria-busy");
    go.textContent = "Find bookings";
  }
}

function statusOf(b: Booking) {
  if (b.status === "cancelled") return { word: "Cancelled", icon: "cancelled", cls: "st-off" };
  if (isPast(b.date, b.time)) return { word: "Played", icon: "check", cls: "st-off" };
  return { word: "Confirmed", icon: "check", cls: "st-on" };
}

function brow(b: Booking) {
  const st = statusOf(b);
  const can = st.word === "Confirmed";
  return `<div class="brow" data-id="${esc(b.id)}">
    <div class="brow-main">
      <p class="body-strong num">${dateLabel(b.date)}, ${b.time}</p>
      <p class="caption ink-muted num">Court ${b.court}, ${b.duration} min, <span class="nowrap">${esc(b.ref)}</span></p>
    </div>
    <p class="bstat body ${st.cls}">${Icon(st.icon, "ic ic-sm")}<span>${st.word}</span></p>
    <div class="brow-acts">${can ? `<button type="button" class="btn btn-text btn-danger" data-cancel>Cancel</button>` : ""}</div>
  </div>`;
}

function renderMine() {
  if (!mineRows.length) {
    mineList.innerHTML = `<div class="state">${Icon("calendar", "ic ic-muted")}
      <div class="state-tx"><p class="body-strong">No bookings for this number.</p><p class="body ink-muted">Check the number, or book a court.</p></div>
      <button type="button" class="btn btn-primary" data-newbook>Book a court</button></div>`;
    return;
  }
  const n = mineRows.filter((b) => statusOf(b).word === "Confirmed").length;
  mineList.innerHTML = `<p class="body ink-muted mine-count num">${n === 1 ? "1 upcoming booking." : `${n} upcoming bookings.`}</p><div class="list">${mineRows.map(brow).join("")}</div>`;
  rise(mineList.querySelectorAll(".brow"), 0.04, 12);
}

mineList.addEventListener("click", async (e) => {
  const t = e.target as HTMLElement;
  if (t.closest("[data-relook]")) return void lookup(minePhone);
  if (t.closest("[data-newbook]")) return void go("pick");
  const row = t.closest<HTMLElement>(".brow");
  if (!row) return;
  const acts = row.querySelector<HTMLElement>(".brow-acts")!;
  if (t.closest("[data-cancel]")) {
    acts.innerHTML = `<span class="body brow-ask">Cancel this booking?</span>
      <button type="button" class="btn btn-text" data-keep>Keep</button>
      <button type="button" class="btn btn-secondary btn-danger-o" data-yes>Yes, cancel</button>`;
    rise(Array.from(acts.children), 0.04, 8);
    acts.querySelector<HTMLElement>("[data-keep]")!.focus();
  } else if (t.closest("[data-keep]")) {
    acts.innerHTML = `<button type="button" class="btn btn-text btn-danger" data-cancel>Cancel</button>`;
    acts.querySelector<HTMLElement>("[data-cancel]")!.focus();
  } else if (t.closest("[data-yes]")) {
    const yes = t.closest<HTMLButtonElement>("[data-yes]")!;
    yes.disabled = true;
    yes.innerHTML = `<span class="spin" aria-hidden="true"></span>Cancelling`;
    try {
      const res = await api.cancel(row.dataset.id!, minePhone);
      const i = mineRows.findIndex((b) => b.id === res.booking.id);
      if (i >= 0) mineRows[i] = res.booking;
      const fresh = document.createElement("div");
      fresh.innerHTML = brow(res.booking);
      const nr = fresh.firstElementChild as HTMLElement;
      row.replaceWith(nr);
      const n = mineRows.filter((b) => statusOf(b).word === "Confirmed").length;
      const c = mineList.querySelector(".mine-count");
      if (c) c.textContent = n === 1 ? "1 upcoming booking." : `${n} upcoming bookings.`;
      if (M) gsap.fromTo(nr.querySelector(".bstat"), { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.4, ease: IN });
      nr.setAttribute("tabindex", "-1");
      nr.focus({ preventScroll: true });
      if (S.avail && res.booking.date === S.date) load(true);
    } catch (err) {
      const a = err as ApiError;
      acts.innerHTML = `<span class="caption brow-err">${Icon("alert", "ic ic-sm")}${esc(a.status === 0 ? "Could not cancel. Try again." : a.message.replace(/\.?$/, "."))}</span>
        <button type="button" class="btn btn-text btn-danger" data-cancel>Cancel</button>`;
    }
  }
});

/* ---------- boot ---------- */
paintDates(false);
paintLen();
paintView();
updateSide();
renderPlayers();
if (location.hash === "#my-bookings") {
  $("step-pick").hidden = true;
  $("step-mine").hidden = false;
  S.step = "mine";
  paintView();
  enterMine();
}
load(true);
{
  const h1 = $("bk-title");
  const photo = document.querySelector<HTMLElement>(".head-photo img")!;
  if (M) {
    const label = h1.textContent ?? "";
    h1.innerHTML = `<span class="mask"><span class="mask-in">${esc(label)}</span></span>`;
    gsap.fromTo(h1.querySelector(".mask-in"), { yPercent: 105 }, { yPercent: 0, duration: 0.9, ease: "expo.out", delay: 0.1, onComplete: () => void (h1.textContent = label) });
    gsap.fromTo(photo, { scale: 1.06 }, { scale: 1, duration: 1.6, ease: "expo.out", clearProps: "transform" });
    rise(Array.from(document.querySelectorAll(".head-lead, .head-facts > div, .seg-view")), 0.07, 16);
    rise(Array.from(document.querySelectorAll("#step-pick .blk, #step-pick .jewel")), 0.09, 32);
  }
}
// first chip scrolled to selection without animation
requestAnimationFrame(() => paintDates(true));

